import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { Spinner } from '@/components/ui/spinner'
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
} from '@/components/ui/attachment'
import { formatFileSize, getFileIcon } from 'utils/chat-attachments'
import { ChatAttachment } from 'types/chat-attachment'

// One file in the composer. Operator files stay 'ready' (uploaded only on send via UploadService).
// Bot files go 'uploading' (HTTP) -> 'processing' (server parsing) -> 'ready'.
export type UploadStatus = 'uploading' | 'processing' | 'ready'

export type ComposerUpload = {
  id: string
  file: File
  status: UploadStatus
  attachment?: ChatAttachment
}

type ComposerAttachmentsProps = {
  uploads: ComposerUpload[]
  onRemove: (id: string) => void
  /** Disables every remove button (e.g. while the message is sending). */
  removeDisabled?: boolean
}

export const ComposerAttachments = ({ uploads, onRemove, removeDisabled }: ComposerAttachmentsProps) => {
  const { t } = useTranslation()
  // Object URLs for image previews; revoked when the upload set changes.
  const previews = useMemo(() => {
    const map = new Map<string, string>()
    uploads.forEach((upload) => {
      if (upload.file.type.startsWith('image/')) map.set(upload.id, URL.createObjectURL(upload.file))
    })
    return map
  }, [uploads])

  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews])

  if (uploads.length === 0) return null

  return (
    <AttachmentGroup className="px-1 pt-1">
      {uploads.map((upload) => {
        const preview = previews.get(upload.id)
        // While the file is not ready the description says so explicitly: the shimmer alone did not
        // tell people the send button is disabled until parsing ends.
        const description =
          upload.status === 'uploading'
            ? t('chatAttachmentUploading')
            : upload.status === 'processing'
              ? t('chatAttachmentProcessing')
              : formatFileSize(upload.file.size)
        return (
          <Attachment
            key={upload.id}
            className="animate-fade-slide-in ring-inset"
            state={upload.status === 'ready' ? 'done' : upload.status}
          >
            <AttachmentMedia variant={preview ? 'image' : 'icon'}>
              {upload.status !== 'ready' ? (
                <Spinner aria-hidden />
              ) : preview ? (
                <img src={preview} alt={upload.file.name} />
              ) : (
                <OdinIcon icon={['fas', getFileIcon(upload.file.name)]} />
              )}
            </AttachmentMedia>
            <AttachmentContent>
              <AttachmentTitle>{upload.file.name}</AttachmentTitle>
              <AttachmentDescription>{description}</AttachmentDescription>
            </AttachmentContent>
            <AttachmentActions>
              <AttachmentAction
                aria-label={upload.file.name}
                disabled={removeDisabled || upload.status === 'uploading'}
                onClick={() => onRemove(upload.id)}
              >
                <OdinIcon icon={['far', 'times']} />
              </AttachmentAction>
            </AttachmentActions>
          </Attachment>
        )
      })}
    </AttachmentGroup>
  )
}
